import React, { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { OverallTradingAccountTemplate } from '../../components/Templates';
import { useOverallTradingAccount } from '../../components/Accounts';
import TradingAccountPrintable from '../../components/Accounts/TradingAccountPrintable';
import { useAppContext } from '../../contexts/Storage';
import { exportToExcel, ExcelColumnConfig } from '../../utils/excelExport';

interface ExcelRow {
  side: string;
  particular: string;
  amount: number;
}

const OverallTradingAccountPage: React.FC = () => {
  const { selectedRecord } = useAppContext();
  const printableRef = useRef<HTMLDivElement>(null);

  const {
    mappedData,
    isLoading,
    handleDateFilter,
    filters,
  } = useOverallTradingAccount();

  const captureAsImage = async (): Promise<string | null> => {
    if (!printableRef.current) return null;
    const wrapper = printableRef.current.parentElement;
    if (wrapper) {
      wrapper.style.overflow = 'visible';
      wrapper.style.height = 'auto';
      wrapper.style.position = 'fixed';
      wrapper.style.left = '0';
      wrapper.style.top = '0';
      wrapper.style.zIndex = '-9999';
      wrapper.style.opacity = '1';
    }
    try {
      return await toPng(printableRef.current, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
      });
    } catch {
      return null;
    } finally {
      if (wrapper) {
        wrapper.style.overflow = 'hidden';
        wrapper.style.height = '0';
        wrapper.style.position = '';
        wrapper.style.left = '';
        wrapper.style.top = '';
        wrapper.style.zIndex = '';
        wrapper.style.opacity = '';
      }
    }
  };

  const handleExportImage = useCallback(async () => {
    const dataUrl = await captureAsImage();
    if (!dataUrl) {
      toast.error('Failed to generate image');
      return;
    }
    const link = document.createElement('a');
    link.download = 'overall_trading_account.png';
    link.href = dataUrl;
    link.click();
    toast.success('Image downloaded');
  }, []);

  const handleExportPDF = useCallback(async () => {
    const dataUrl = await captureAsImage();
    if (!dataUrl) {
      toast.error('Failed to generate PDF');
      return;
    }
    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });
    const pixelRatio = 2;
    const pxToMm = 0.264583;
    const imgWidthMm = (img.width / pixelRatio) * pxToMm;
    const imgHeightMm = (img.height / pixelRatio) * pxToMm;
    const isLandscape = imgWidthMm > imgHeightMm;
    const pdf = new jsPDF({
      orientation: isLandscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [imgWidthMm + 20, imgHeightMm + 20],
    });
    pdf.addImage(dataUrl, 'PNG', 10, 10, imgWidthMm, imgHeightMm);
    pdf.save('overall_trading_account.pdf');
    toast.success('PDF downloaded');
  }, []);

  const handleExportExcel = useCallback(() => {
    if (!mappedData) {
      toast.error('No data to export');
      return;
    }

    const rows: ExcelRow[] = [];
    mappedData.debitSide.items.forEach((item) => {
      rows.push({ side: 'DR', particular: item.name, amount: item.amount });
    });
    rows.push({ side: 'DR', particular: 'Total', amount: mappedData.debitSide.total });
    mappedData.creditSide.items.forEach((item) => {
      rows.push({ side: 'CR', particular: item.name, amount: item.amount });
    });
    rows.push({ side: 'CR', particular: 'Total', amount: mappedData.creditSide.total });

    const columns: ExcelColumnConfig<ExcelRow>[] = [
      { key: 'side', header: 'Side' },
      { key: 'particular', header: 'Particular' },
      { key: 'amount', header: 'Amount', transform: (v: number) => `$${v.toFixed(2)}` },
    ];

    exportToExcel({
      data: rows,
      columns,
      fileName: 'overall_trading_account',
      sheetName: 'Overall Trading Account',
      includeTimestamp: true,
    });
    toast.success('Excel downloaded');
  }, [mappedData]);

  const handlePrint = useCallback(() => {
    if (!printableRef.current) {
      toast.error('Nothing to print');
      return;
    }
    const htmlContent = printableRef.current.outerHTML;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      toast.error('Failed to prepare print content');
      document.body.removeChild(iframe);
      return;
    }
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Overall Trading Account</title>
          <style>
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body style="margin:0;padding:0;">
          ${htmlContent}
        </body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 100);
  }, []);

  if (!selectedRecord) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-grey_dark">Please select a record to view the overall trading account</p>
        </div>
      </div>
    );
  }

  const totalEntries = (mappedData?.debitSide.items.length || 0) + (mappedData?.creditSide.items.length || 0);

  return (
    <OverallTradingAccountTemplate
      title="Overall Trading Account"
      description="Summary of all trading activities"
      data={mappedData}
      loading={isLoading}
      onExportImage={handleExportImage}
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
      onPrint={handlePrint}
      showDateRange={true}
      onDateRangeChanged={handleDateFilter}
      emptyMessage="No trading account data available"
      entryCount={totalEntries}
      extraContent={
        <div style={{ overflow: 'hidden', height: 0 }}>
          <TradingAccountPrintable
            ref={printableRef}
            title="Overall Trading Account"
            data={mappedData}
            startDate={filters.start_date?.split(' ')[0]}
            endDate={filters.end_date?.split(' ')[0]}
          />
        </div>
      }
    />
  );
};

export default OverallTradingAccountPage;
